import clsx from "clsx";
import type { HTMLAttributes } from "react";
import {
  getAniLogoLockupStyles,
  type AniLogoSize,
} from "@/components/branding/aniLogoLockup";
import { useColorSchemeWithFallback } from "@/context/ColorSchemeContext";

export type LogoVariant = "light" | "dark" | "auto";
export type LogoSize = AniLogoSize;

interface LogoProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  iconOnly?: boolean;
  /** Preset lockup scale — mark, wordmark, and gap stay proportional. */
  size?: LogoSize;
  /** Force light/dark wordmark styling, or follow the active color scheme. */
  variant?: LogoVariant;
  "data-testid"?: string;
}

export const Logo = ({
  className = "",
  iconOnly = false,
  size = "lg",
  variant = "auto",
  "data-testid": testId,
  ...rest
}: LogoProps) => {
  const { isDark: themeIsDark } = useColorSchemeWithFallback();
  const isDark = variant === "auto" ? themeIsDark : variant === "dark";
  const lockup = getAniLogoLockupStyles(size);

  if (iconOnly) {
    return (
      <div
        className={clsx("inline-flex shrink-0 items-center justify-center", className)}
        data-testid={testId}
        {...rest}
      >
        <img
          src="/images/logo.svg"
          alt="Ani Tech"
          className="h-full w-full object-contain"
        />
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "inline-flex shrink-0 min-w-0 items-center cursor-pointer",
        className,
      )}
      style={lockup.container}
      data-testid={testId}
      {...rest}
    >
      <img
        src="/images/logo.svg"
        alt="Ani Tech"
        className="shrink-0 object-contain"
        style={lockup.mark}
      />
      <img
        src="/images/logo_text.svg"
        alt="Ani Tech"
        className={clsx("w-auto shrink-0", isDark && "brightness-0 invert")}
        style={lockup.wordmark}
        data-testid={testId ? `${testId}-wordmark` : undefined}
      />
    </div>
  );
};

export default Logo;
